import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui";
import {
  Wrench,
  Hammer,
  Truck,
  Sparkles,
  TreePine,
  PaintBucket,
  Wifi,
  Monitor,
  ArrowRight,
  Clock,
  Shield,
  Star,
} from "lucide-react";
import { cn } from "../../lib/utils";

const services = [
  {
    id: "furniture-assembly",
    name: "Furniture Assembly",
    tagline: "Built to last",
    description:
      "Professional assembly for all types of furniture — from flatpacks to complex units.",
    icon: Hammer,
    priceRange: "$50 – $150",
    duration: "1–3 hrs",
    image: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&h=500&fit=crop",
  },
  {
    id: "mounting",
    name: "TV & Wall Mounting",
    tagline: "Perfectly aligned",
    description:
      "Secure mounting for TVs, shelves, mirrors, and artwork with hidden cable management.",
    icon: Monitor,
    priceRange: "$75 – $200",
    duration: "1–2 hrs",
    image: "https://images.unsplash.com/photo-1593784991095-a205069470b6?w=800&h=500&fit=crop",
  },
  {
    id: "moving",
    name: "Moving Assistance",
    tagline: "Stress-free moves",
    description:
      "Careful handling and transport of furniture and household items, big or small.",
    icon: Truck,
    priceRange: "$100 – $500",
    duration: "2–8 hrs",
    image: "https://images.unsplash.com/photo-1600518464441-9154a4dea21b?w=800&h=500&fit=crop",
  },
  {
    id: "cleaning",
    name: "Cleaning Services",
    tagline: "Spotless results",
    description:
      "Deep cleans, move-in/out cleans, and recurring maintenance packages.",
    icon: Sparkles,
    priceRange: "$80 – $250",
    duration: "2–4 hrs",
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&h=500&fit=crop",
  },
  {
    id: "outdoor",
    name: "Outdoor & Lawn Care",
    tagline: "Curb appeal, delivered",
    description:
      "Lawn mowing, hedge trimming, leaf removal, garden care and seasonal upkeep.",
    icon: TreePine,
    priceRange: "$60 – $200",
    duration: "2–4 hrs",
    image: "https://images.unsplash.com/photo-1558904541-efa843a96f01?w=800&h=500&fit=crop",
  },
  {
    id: "home-repairs",
    name: "Home Repairs",
    tagline: "Fix anything",
    description:
      "General handyman services — plumbing fixes, drywall patches, door adjustments and more.",
    icon: Wrench,
    priceRange: "$75 – $300",
    duration: "1–4 hrs",
    image: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800&h=500&fit=crop",
  },
  {
    id: "painting",
    name: "Painting",
    tagline: "Fresh new look",
    description:
      "Interior and exterior painting, accent walls, cabinet refinishing and color consultation.",
    icon: PaintBucket,
    priceRange: "$150 – $1,000",
    duration: "4–16 hrs",
    image: "https://images.unsplash.com/photo-1562259929-b4e1fd3aef09?w=800&h=500&fit=crop",
  },
  {
    id: "smart-home",
    name: "Smart Home Setup",
    tagline: "Live smarter",
    description:
      "Installation and configuration of smart locks, thermostats, cameras, and voice assistants.",
    icon: Wifi,
    priceRange: "$100 – $400",
    duration: "2–4 hrs",
    image: "https://images.unsplash.com/photo-1558002038-1055907df827?w=800&h=500&fit=crop",
  },
];

export function Services() {
  const { t } = useTranslation('services');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <div>
      {/* ── Compact Header ── */}
      <section className="border-b bg-background">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-4xl font-light tracking-tight">{t('title')}</h1>
              <p className="text-sm text-muted-foreground mt-0.5">{t('subtitle')}</p>
            </div>
            <div className="flex flex-wrap gap-4">
              {[
                { icon: Shield, key: 'verified' },
                { icon: Star, key: 'rating' },
                { icon: Clock, key: 'sameDay' },
              ].map(({ icon: Icon, key }) => (
                <div key={key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Icon className="h-3.5 w-3.5" />
                  <span>{t(`trust.${key}`)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Services Grid ── */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-12">
        {/* Featured large card — first service */}
        {(() => {
          const FeaturedIcon = services[0].icon;
          return (
        <div
          className="group relative rounded-3xl overflow-hidden border cursor-pointer mb-8 animate-fade-in-up"
          onMouseEnter={() => setHoveredId(services[0].id)}
          onMouseLeave={() => setHoveredId(null)}
        >
          <div className="grid md:grid-cols-2">
            <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden">
              <img
                src={services[0].image}
                alt={services[0].name}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-4">
                <FeaturedIcon className="h-5 w-5 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground tracking-widest uppercase">
                  {services[0].tagline}
                </span>
              </div>
              <h2 className="text-3xl font-light tracking-tight">
                {services[0].name}
              </h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                {services[0].description}
              </p>
              <div className="flex items-center gap-6 mt-6 text-sm text-muted-foreground">
                <span>{t('from')} {services[0].priceRange}</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {services[0].duration}
                </span>
              </div>
              <div className="mt-8">
                <Button className="rounded-full px-6 gap-2 group/btn">
                  {t('button.bookNow')}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
          );
        })()}

        {/* Smaller service cards */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {services.slice(1).map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={service.id}
                className={cn(
                  "group relative rounded-2xl border bg-card overflow-hidden cursor-pointer",
                  "transition-all duration-500 hover:shadow-xl hover:-translate-y-1",
                  "opacity-0 animate-fade-in-up"
                )}
                style={{
                  animationDelay: `${index * 0.07}s`,
                  animationFillMode: "forwards",
                }}
                onMouseEnter={() => setHoveredId(service.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                {/* Image */}
                <div className="relative aspect-[3/2] overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-3 right-3 h-9 w-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                    <ArrowRight className="h-4 w-4 text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-[11px] font-medium text-muted-foreground tracking-widest uppercase">
                      {service.tagline}
                    </span>
                  </div>
                  <h3 className="font-normal text-base">{service.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                    {service.description}
                  </p>

                  <div className="flex items-center justify-between mt-4 pt-4 border-t text-xs text-muted-foreground">
                    <span>{service.priceRange}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {service.duration}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="border-t">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center max-w-xl mx-auto animate-fade-in">
            <h2 className="text-3xl font-light tracking-tight">
              {t('cta.title')}
            </h2>
            <p className="mt-4 text-muted-foreground">
              {t('cta.description')}
            </p>
            <div className="mt-8 flex items-center justify-center gap-4">
              <Button className="rounded-full px-6 gap-2">
                {t('button.getRecommendation')}
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="rounded-full px-6">
                {t('button.browseAll')}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
